/* ==========================================================================
   Solutions & Architecture Page Component
   Using the exact same visual architecture graph as Home Page - 100% Multi-Language i18n
   ========================================================================== */

import { t, getLocale } from '../locales/i18n.js';
import { openArticleModal } from '../components/ArticleModal.js';
import { renderArchitectureGraph } from '../components/ArchitectureGraph.js';
import { createYouTubeEmbed } from '../components/YouTubeLazyEmbed.js';

const solutionsTranslations = {
  en: {
    videoEyebrow: 'OFFICIAL VIDEO SHOWCASE',
    videoTitle: 'See Sound Event Detector in Action',
    videoDesc: 'Watch our official introductory promo video and technical demonstrations showcasing real-time acoustic AI detection, sub-second PTZ camera auto-tracking, and seamless integration into municipal security centers.',
    subSecondTitle: 'Sub-Second Triggering',
    subSecondDesc: 'Neural event classification and emergency notification in less than 0.4 seconds.',
    slewTitle: 'Automated Slew-to-Cue',
    slewDesc: 'Automatically pivots PTZ cameras to exact sound blast and aggression coordinates.',
    gdprTitle: 'Privacy-by-Design Architecture',
    gdprDesc: 'Edge AI processing without saving conversations or human voice audio.',
    demoHeading: 'Technical Capability & Integration Demonstrations',
    v1Title: 'Sound Event Localization & Fast Response',
    v1Desc: 'Sub-degree acoustic triangulation and automated PTZ camera slewing during emergencies.',
    v2Title: 'Coverage Design & Detection Optimization',
    v2Desc: 'Acoustic site modeling and sensor placement guidelines for complex physical environments.',
    v3Title: 'JALUD & KISS: Instant Crisis Response',
    v3Desc: 'Direct integration into mass notification, automated emergency calls, and SMS dispatch.',
    vmsEyebrow: 'ENTERPRISE INTEGRATION MATRIX & OPEN PROTOCOLS',
    vmsTitle: 'Supported Video Management Systems (VMS) & Platforms',
    vmsDesc: 'Sound Event Detector integrates seamlessly into leading global VMS platforms, PSIM command consoles, and emergency mass notification networks.',
    ecosystemTitle: 'Supported VMS, PSIM & Mass Notification Ecosystem',
    protoTitle: 'Open Architecture: Seamless Integration into ANY Security Platform',
    protoDesc: 'SED is engineered with an open protocol engine. In addition to our pre-built native plugins, SED broadcasts standardized telemetry and triggers over <strong>SAPIENT (NATO STANREC 4869 / BSI Flex 335)</strong>, <strong>ONVIF (Profile S & M)</strong>, <strong>ConnectID</strong>, <strong>SIA DC-09</strong>, <strong>REST API / Webhooks</strong>, <strong>MQTT</strong>, and <strong>SIP</strong>. This guarantees instant interoperability with defense command networks, municipal centers, and proprietary security platforms worldwide.',
    consultBtn: 'Consult System Architecture with Engineers →',
    visitPrefix: 'Visit',
    customTitle: 'Custom & 3rd Party Systems',
    customBadge: 'Universal Adapters',
    customDesc: 'Connect any proprietary dispatch console, defense C2 system, or physical security software with standard protocol adapters.',
    customBtn: 'Request Protocol Specs →',
    vmsAxis: 'Runs directly on ARTPEC-8/9 cameras or bridges into Axis Camera Station with auto PTZ slewing.',
    vmsMilestone: 'Full Smart Client alarm integration with live acoustic sound classification and map overlays.',
    vmsGenetec: 'Direct event injection for automated camera switching, access lockdowns, and operator workflows.',
    vmsHanwha: 'Real-time HTTP event metadata integration with WAVE desktop layout bookmarks and alerts.',
    vmsBosch: 'Seamless integration with BVMS alarm engine and camera sequencing triggers via ONVIF/REST.',
    vmsAvigilon: 'ONVIF Profile S / Profile M metadata integration with Focus of Attention (FoA) visual mapping.',
    vmsHikCentral: 'Direct alarm and event linking into HikCentral central monitoring and e-map dispatch console.',
    vmsGeutebrueck: 'Deep integration with high-security German video management platform Geutebrück G-Core & G-Sim.',
    vmsAteas: 'Real-time acoustic telemetry and camera mapping into ATEAS Security VMS software suite.',
    vmsIvisec: 'Turnkey integration with iVISEC security and CCTV management software developed by ZLD.',
    vmsLatis: 'Seamless integration with LATIS security, alarm receiving, and access control platforms by FIDES.',
    vmsDroneGuard: 'Acoustic drone signature integration into DroneGuard multi-sensor counter-UAV defense systems.',
    vmsKiss: 'Instant automated mass paging, voice calling, and SMS notification for emergency response teams during critical incidents.',
    vmsSphereLink: 'SED acoustic AI software embedded natively into Tondo SphereLink outdoor lighting controllers and smart city infrastructure.'
  },
  cs: {
    videoEyebrow: 'OFICIÁLNÍ VIDEOUKÁZKY',
    videoTitle: 'Podívejte se na Sound Event Detector v akci',
    videoDesc: 'Sledujte naše oficiální úvodní video a technické ukázky představující akustickou AI detekci v reálném čase, automatické navádění PTZ kamer pod 1 sekundu a bezproblémovou integraci do městských bezpečnostních center.',
    subSecondTitle: 'Detekce a upozornění pod 0,4 s',
    subSecondDesc: 'Klasifikace neuronovou sítí a nouzové upozornění za méně než 0,4 sekundy.',
    slewTitle: 'Automatické navádění PTZ kamer',
    slewDesc: 'Automaticky natáčí PTZ kamery přímo na přesné souřadnice výstřelu či agrese.',
    gdprTitle: 'Ochrana soukromí (Privacy by Design)',
    gdprDesc: 'Edge AI zpracování na čipu bez ukládání hlasových záznamů a konverzací.',
    demoHeading: 'Technické ukázky a integrace v praxi',
    v1Title: 'Lokalizace zvukových událostí a rychlá reakce',
    v1Desc: 'Sub-stupňová akustická triangulace a automatické navádění PTZ kamer v krizových situacích.',
    v2Title: 'Návrh pokrytí a optimalizace detekce',
    v2Desc: 'Akustické modelování lokality a doporučení pro rozmístění senzorů v členitém prostředí.',
    v3Title: 'JALUD a KISS: Okamžitá krizová reakce',
    v3Desc: 'Přímá integrace do hromadného varování, automatických tísňových volání a SMS svolávání.',
    vmsEyebrow: 'INTEGRAČNÍ MATICE & OTEVŘENÉ PROTOKOLY',
    vmsTitle: 'Podporované systémy správy videa (VMS) a platformy',
    vmsDesc: 'Sound Event Detector se bezproblémově integruje do předních světových VMS systémů, velínů PSIM a sítí hromadného varování.',
    ecosystemTitle: 'Podporovaný ekosystém VMS, PSIM a hromadného varování',
    protoTitle: 'Otevřená architektura: Bezproblémová integrace do JAKÉKOLIV bezpečnostní platformy',
    protoDesc: 'SED je navržen s otevřeným protokolovým jádrem. Kromě nativních pluginů pro kamery SED vysílá standardizovaná telemetrická data a poplachy přes protokoly <strong>SAPIENT (NATO STANREC 4869 / BSI Flex 335)</strong>, <strong>ONVIF (Profile S & M)</strong>, <strong>ConnectID</strong>, <strong>SIA DC-09</strong>, <strong>REST API / Webhooky</strong>, <strong>MQTT</strong> a <strong>SIP</strong>. To zaručuje okamžitou interoperabilitu s velitelskými sítěmi obrany, městskými velíny i zákaznickými platformami po celém světě.',
    consultBtn: 'Konzultujte architekturu systému s techniky →',
    visitPrefix: 'Navštívit',
    customTitle: 'Vlastní systémy & systémy třetích stran',
    customBadge: 'Univerzální adaptéry',
    customDesc: 'Připojte jakýkoli vlastní dispečink, armádní C2 systém nebo bezpečnostní software pomocí standardních protokolových adaptérů.',
    customBtn: 'Vyžádat specifikaci protokolů →',
    vmsAxis: 'Běží přímo na kamerách ARTPEC-8/9 nebo se integruje do Axis Camera Station s automatickým naváděním PTZ.',
    vmsMilestone: 'Plná integrace poplachů do Smart Clienta s živou akustickou klasifikací a zobrazením na mapě.',
    vmsGenetec: 'Přímé vkládání událostí pro automatické přepínání kamer, uzamykání vstupů a pracovní postupy dispečera.',
    vmsHanwha: 'Předávání metadat v reálném čase přes HTTP s tvorbou záložek v rozvržení WAVE a notifikacemi.',
    vmsBosch: 'Integrace s poplachovým systémem BVMS a sekvenčním spouštěním kamer přes ONVIF/REST.',
    vmsAvigilon: 'Předávání poplachů v reálném čase, spouštění pravidel pro přepínání kamer a Focus of Attention (FoA) mapování.',
    vmsHikCentral: 'Přímé propojení poplachů do centrálního dohledu HikCentral a e-map dispečerského pultu.',
    vmsGeutebrueck: 'Hluboká integrace s německou vysoko-bezpečnostní platformou správy videa Geutebrück G-Core & G-Sim.',
    vmsAteas: 'Přenos akustické telemetrie a mapování kamer do softwarového balíku ATEAS Security VMS v reálném čase.',
    vmsIvisec: 'Integrované řešení s bezpečnostním a CCTV řídicím softwarem iVISEC vyvinutým společností ZLD.',
    vmsLatis: 'Bezproblémová integrace s bezpečnostními, poplachovými a přístupovými platformami LATIS od společnosti FIDES.',
    vmsDroneGuard: 'Integrace akustického podpisu dronů do více-senzorových antidronových obranných systémů DroneGuard.',
    vmsKiss: 'Okamžité automatické hromadné rozesílání zpráv, hlasová volání a SMS svolávání záchranných týmů při krizových incidentech.',
    vmsSphereLink: 'Akustický AI software SED vestavěný nativně do venkovních řídicích jednotek veřejného osvětlení Tondo SphereLink a městské IoT infrastruktury.'
  },
  de: {
    videoEyebrow: 'OFFIZIELLE VIDEOPRÄSENTATION',
    videoTitle: 'Sound Event Detector in Aktion erleben',
    videoDesc: 'Sehen Sie unser offizielles Einführungsvideo und technische Demonstrationen zu akustischer KI-Erkennung in Echtzeit, sekundenschneller PTZ-Kamerasteuerung und nahtloser VMS-Integration.',
    subSecondTitle: 'Auslösung in unter 0,4 Sekunden',
    subSecondDesc: 'Neuronale Ereignisklassifizierung und Notfallbenachrichtigung in weniger als 0,4 s.',
    slewTitle: 'Automatisierte PTZ-Kamerasteuerung',
    slewDesc: 'Schwenkt PTZ-Kameras automatisch auf die exakten Koordinaten von Schüssen oder Aggressionen.',
    gdprTitle: 'Datenschutz durch Technikgestaltung',
    gdprDesc: 'Edge-KI-Verarbeitung auf dem Chip ohne Aufzeichnung von Sprach- oder Gesprächsdaten.',
    demoHeading: 'Technische Fähigkeiten & Praxis-Demonstrationen',
    v1Title: 'Schallereignis-Lokalisierung & Schnelle Reaktion',
    v1Desc: 'Subgrad-akustische Triangulation und automatisches Schwenken von PTZ-Kameras bei Notfällen.',
    v2Title: 'Abdeckungsdesign & Detektionsoptimierung',
    v2Desc: 'Akustische Standortmodellierung und Sensorplatzierungsrichtlinien für komplexe Umgebungen.',
    v3Title: 'JALUD & KISS: Sofortige Krisenreaktion',
    v3Desc: 'Direkte Integration in Massenbenachrichtigung, automatisierte Notrufe und SMS-Alarmierung.',
    vmsEyebrow: 'ENTERPRISE-INTEGRATIONSMATRIX & OFFENE PROTOKOLLE',
    vmsTitle: 'Unterstützte Videomanagementsysteme (VMS) & Plattformen',
    vmsDesc: 'Sound Event Detector integriert sich nahtlos in weltweit führende VMS-Plattformen, PSIM-Leitstände und Alarmierungsnetzwerke.',
    ecosystemTitle: 'Unterstütztes VMS-, PSIM- & Alarmierungs-Ökosystem',
    protoTitle: 'Offene Architektur: Nahtlose Integration in JEDE Sicherheitsplattform',
    protoDesc: 'SED basiert auf einer offenen Protokoll-Engine. Neben nativen Plugins überträgt SED standardisierte Telemetrie über <strong>SAPIENT (NATO STANREC 4869)</strong>, <strong>ONVIF (Profile S & M)</strong>, <strong>SIA DC-09</strong>, <strong>REST API</strong>, <strong>MQTT</strong> und <strong>SIP</strong>.',
    consultBtn: 'Systemarchitektur mit Ingenieuren besprechen →',
    visitPrefix: 'Besuchen Sie',
    customTitle: 'Eigene & Drittanbietersysteme',
    customBadge: 'Universelle Adapter',
    customDesc: 'Verbinden Sie jede proprietäre Leitstelle oder Sicherheitssoftware über Standardprotokolle.',
    customBtn: 'Protokollspezifikationen anfordern →',
    vmsAxis: 'Läuft direkt auf ARTPEC-8/9-Kameras oder integriert sich in Axis Camera Station mit automatischer PTZ-Nachführung.',
    vmsMilestone: 'Vollständige Smart-Client-Alarmintegration mit akustischer Live-Klassifizierung und Karten-Overlay.',
    vmsGenetec: 'Direkte Ereignisinjektion für automatisches Kameraschalten, Sperren und Leitstellen-Workflows.',
    vmsHanwha: 'Echtzeit-HTTP-Metadatenübertragung mit WAVE-Desktop-Lesezeichen und Alarmmeldungen.',
    vmsBosch: 'Nahtlose Integration in die BVMS-Alarm-Engine und Kameraschaltung über ONVIF/REST.',
    vmsAvigilon: 'ONVIF Profile S/M Metadaten-Integration mit Focus of Attention (FoA) Visualisierung.',
    vmsHikCentral: 'Direkte Alarm- und Ereignisverknüpfung in die zentrale HikCentral-Leitwarte und E-Map.',
    vmsGeutebrueck: 'Tiefenintegration in die hochsichere deutsche Videomanagement-Plattform Geutebrück G-Core & G-Sim.',
    vmsAteas: 'Echtzeit-Telemetrie und Kamerakartierung in die ATEAS Security VMS Software-Suite.',
    vmsIvisec: 'Schlüsselfertige Integration in die iVISEC-Sicherheits- und CCTV-Verwaltungssoftware von ZLD.',
    vmsLatis: 'Nahtlose Integration in LATIS-Sicherheits- und Zutrittsplattformen von FIDES.',
    vmsDroneGuard: 'Integration akustischer Drohnensignaturen in DroneGuard-Mehrsensor-Abwehrsysteme.',
    vmsKiss: 'Sofortige automatisierte Massenbenachrichtigung, Sprachanrufe und SMS-Alarmierung.',
    vmsSphereLink: 'SED-KI nativ eingebettet in Tondo SphereLink Straßenbeleuchtungs-Controller und Smart-City-Module.'
  },
  es: {
    videoEyebrow: 'PRESENTACIÓN OFICIAL EN VÍDEO',
    videoTitle: 'Vea Sound Event Detector en Acción',
    videoDesc: 'Vea nuestro vídeo promocional oficial y demostraciones técnicas sobre detección acústica mediante IA en tiempo real, seguimiento automático PTZ en menos de 1 segundo e integración en centros de seguridad.',
    subSecondTitle: 'Activación en menos de 0,4 s',
    subSecondDesc: 'Clasificación de eventos mediante red neuronal y alerta de emergencia en menos de 0,4 segundos.',
    slewTitle: 'Giro automático de cámaras PTZ',
    slewDesc: 'Orienta automáticamente las cámaras PTZ hacia las coordenadas exactas del disparo o agresión.',
    gdprTitle: 'Privacidad desde el Diseño (GDPR)',
    gdprDesc: 'Procesamiento de IA en el chip perimetral sin almacenar conversaciones ni grabaciones de voz.',
    demoHeading: 'Demostraciones de Capacidad Técnica e Integración',
    v1Title: 'Localización de Eventos Sonoros y Respuesta Rápida',
    v1Desc: 'Triangulación acústica de precisión sub-grado y giro automático de cámaras PTZ en emergencias.',
    v2Title: 'Diseño de Cobertura y Optimización de Detección',
    v2Desc: 'Modelado acústico del emplazamiento y pautas de colocación de sensores en entornos complejos.',
    v3Title: 'JALUD y KISS: Respuesta Inmediata a Crisis',
    v3Desc: 'Integración directa en sistemas de alerta masiva, llamadas de emergencia automatizadas y SMS.',
    vmsEyebrow: 'MATRIZ DE INTEGRACIÓN Y PROTOCOLOS ABIERTOS',
    vmsTitle: 'Sistemas de Gestión de Vídeo (VMS) y Plataformas Compatibles',
    vmsDesc: 'Sound Event Detector se integra a la perfección en los principales sistemas VMS, centros de control PSIM y redes de notificación de emergencia.',
    ecosystemTitle: 'Ecosistema Compatible de VMS, PSIM y Notificación Masiva',
    protoTitle: 'Arquitectura Abierta: Integración Fluida en CUALQUIER Plataforma',
    protoDesc: 'SED está diseñado con un motor de protocolos abiertos. Además de plugins nativos, SED transmite telemetría y alarmas estandarizadas mediante <strong>SAPIENT (OTAN STANREC 4869)</strong>, <strong>ONVIF (Profile S & M)</strong>, <strong>SIA DC-09</strong>, <strong>REST API / Webhooks</strong>, <strong>MQTT</strong> y <strong>SIP</strong>.',
    consultBtn: 'Consultar arquitectura del sistema con ingenieros →',
    visitPrefix: 'Visitar',
    customTitle: 'Sistemas Propios y de Terceros',
    customBadge: 'Adaptadores Universales',
    customDesc: 'Conecte cualquier centro de control, sistema C2 de defensa o software de seguridad física con adaptadores estándar.',
    customBtn: 'Solicitar especificaciones de protocolos →',
    vmsAxis: 'Se ejecuta directamente en cámaras ARTPEC-8/9 o se integra en Axis Camera Station con giro automático PTZ.',
    vmsMilestone: 'Integración completa de alarmas en Smart Client con clasificación acústica en vivo y mapa interactivo.',
    vmsGenetec: 'Inyección directa de eventos para cambio de cámaras, cierre de accesos y flujos de operador.',
    vmsHanwha: 'Transmisión de metadatos HTTP en tiempo real con marcadores WAVE y notificaciones de alerta.',
    vmsBosch: 'Integración fluida con el motor de alarmas BVMS y secuencias de cámaras vía ONVIF/REST.',
    vmsAvigilon: 'Metadatos ONVIF Profile S/M con visualización Focus of Attention (FoA).',
    vmsHikCentral: 'Vinculación directa de eventos en la consola central HikCentral y mapas interactivos.',
    vmsGeutebrueck: 'Integración avanzada con la plataforma alemana de alta seguridad Geutebrück G-Core & G-Sim.',
    vmsAteas: 'Telemetría acústica en tiempo real y asignación de cámaras en la suite ATEAS Security VMS.',
    vmsIvisec: 'Solución integrada con el software de gestión de seguridad y CCTV iVISEC de ZLD.',
    vmsLatis: 'Integración perfecta con las plataformas de seguridad y control de acceso LATIS de FIDES.',
    vmsDroneGuard: 'Integración de firmas acústicas de drones en los sistemas antidrón multisensor DroneGuard.',
    vmsKiss: 'Notificación masiva automatizada, llamadas de voz y despacho SMS a equipos de emergencia.',
    vmsSphereLink: 'Software de IA acústica SED integrado en controladores de alumbrado público Tondo SphereLink.'
  },
  it: {
    videoEyebrow: 'PRESENTAZIONE VIDEO UFFICIALE',
    videoTitle: 'Guarda Sound Event Detector in Azione',
    videoDesc: 'Guarda il nostro video di presentazione ufficiale e le dimostrazioni tecniche con rilevamento acustico AI in tempo reale, puntamento automatico delle telecamere PTZ in meno di 1 secondo e integrazione nei centri di comando.',
    subSecondTitle: 'Rilevamento in meno di 0,4 s',
    subSecondDesc: 'Classificazione neurale degli eventi e notifica di emergenza in meno di 0,4 secondi.',
    slewTitle: 'Puntamento Automatico Telecamere PTZ',
    slewDesc: 'Orienta automaticamente le telecamere PTZ verso le coordinate esatte dello sparo o dell\'aggressione.',
    gdprTitle: 'Privacy by Design (GDPR)',
    gdprDesc: 'Elaborazione AI on-chip senza memorizzazione di conversazioni o registrazioni vocali umane.',
    demoHeading: 'Dimostrazioni di Capacità Tecnica e Integrazione',
    v1Title: 'Localizzazione Eventi Sonori e Risposta Rapida',
    v1Desc: 'Triangolazione acustica ad alta precisione e rotazione automatica delle telecamere PTZ in situazioni critiche.',
    v2Title: 'Progettazione Copertura e Ottimizzazione Rilevamento',
    v2Desc: 'Modellazione acustica del sito e linee guida per il posizionamento dei sensori in ambienti complessi.',
    v3Title: 'JALUD & KISS: Risposta Immediata alle Crisi',
    v3Desc: 'Integrazione diretta per notifiche di massa, chiamate automatiche di emergenza e invio SMS.',
    vmsEyebrow: 'MATRICE DI INTEGRAZIONE E PROTOCOLLI APERTI',
    vmsTitle: 'Sistemi di Gestione Video (VMS) e Piattaforme Supportate',
    vmsDesc: 'Sound Event Detector si integra perfettamente con le principali piattaforme VMS, centrali PSIM e reti di allarme del mondo.',
    ecosystemTitle: 'Ecosistema Supportato di VMS, PSIM e Notifiche di Massa',
    protoTitle: 'Architettura Aperta: Integrazione Perfetta su QUALSIASI Piattaforma',
    protoDesc: 'SED è progettato con un motore a protocolli aperti. Oltre ai plugin nativi, SED trasmette telemetria e allarmi standardizzati via <strong>SAPIENT (NATO STANREC 4869)</strong>, <strong>ONVIF (Profile S & M)</strong>, <strong>SIA DC-09</strong>, <strong>REST API / Webhook</strong>, <strong>MQTT</strong> e <strong>SIP</strong>.',
    vmsMilestone: 'Integrazione ufficiale con Milestone Systems XProtect con supporto analitica e slewing PTZ.',
    vmsGenetec: 'Integrazione avanzata con Genetec Security Center via protocolli IP e webhooks.',
    vmsHanwha: 'App embedded nativa per telecamere Hanwha Vision (chipset Wisenet 7 e 9).',
    vmsAxis: 'App nativa ACAP per telecamere Axis Communications con rilevamento a bordo chip.',
    vmsHikvision: 'Applicazione HEOP integrata per telecamere smart Hikvision con classificazione locale.',
    vmsAvigilon: 'Metadati allarme in tempo reale per Avigilon Control Center (ACC) e telecamere Motorola Solutions.',
    vmsGeutebrueck: 'Integrazione avanzata con la piattaforma di alta sicurezza tedesca Geutebrück G-Core & G-Sim.',
    vmsAteas: 'Telemetria acustica in tempo reale e assegnazione telecamere nella suite ATEAS Security VMS.',
    vmsIvisec: 'Soluzione integrata con il software di gestione della sicurezza e CCTV iVISEC di ZLD.',
    vmsLatis: 'Integrazione perfetta con le piattaforme di sicurezza e controllo accessi LATIS di FIDES.',
    vmsDroneGuard: 'Integrazione delle firme acustiche dei droni nei sistemi anti-drone multisensore DroneGuard.',
    vmsKiss: 'Notifica di massa automatizzata, chiamate vocali e invio SMS a squadre di emergenza.',
    vmsSphereLink: 'Software AI acustico SED integrato nei controller per illuminazione pubblica Tondo SphereLink.'
  },
  pt: {
    videoEyebrow: 'APRESENTAÇÃO OFICIAL EM VÍDEO',
    videoTitle: 'Veja o Sound Event Detector em Ação',
    videoDesc: 'Assista ao nosso vídeo de apresentação oficial e demonstrações técnicas com detecção acústica por IA em tempo real, direcionamento automático de câmeras PTZ em menos de 1 segundo e integração em centros de comando.',
    subSecondTitle: 'Disparo em menos de 0,4 s',
    subSecondDesc: 'Classificação neural de eventos e notificação de emergência em menos de 0,4 segundos.',
    slewTitle: 'Direcionamento Automático de Câmeras PTZ',
    slewDesc: 'Orienta automaticamente câmeras PTZ para as coordenadas exatas do disparo ou agressão.',
    gdprTitle: 'Privacidade por Design (GDPR / LGPD)',
    gdprDesc: 'Processamento de IA no chip de borda sem armazenar conversas ou gravações de voz humana.',
    demoHeading: 'Demonstrações de Capacidade Técnica e Integração',
    v1Title: 'Localização de Eventos Sonoros e Resposta Rápida',
    v1Desc: 'Triangulação acústica de alta precisão e rotação automática de câmeras PTZ em situações críticas.',
    v2Title: 'Projeto de Cobertura e Otimização de Deteção',
    v2Desc: 'Modelagem acústica do local e diretrizes de posicionamento de sensores em ambientes complexos.',
    v3Title: 'JALUD & KISS: Resposta Imediata a Crises',
    v3Desc: 'Integração direta em notificação em massa, chamadas automáticas de emergência e despacho por SMS.',
    vmsEyebrow: 'MATRIZ DE INTEGRAÇÃO E PROTOCOLOS ABERTOS',
    vmsTitle: 'Sistemas de Gestão de Vídeo (VMS) e Plataformas Suportadas',
    vmsDesc: 'O Sound Event Detector integra-se perfeitamente às principais plataformas VMS, centrais PSIM e redes de alerta de emergência do mundo.',
    ecosystemTitle: 'Ecossistema Suportado de VMS, PSIM e Notificação em Massa',
    protoTitle: 'Arquitetura Aberta: Integração Perfeita em QUALQUER Plataforma',
    protoDesc: 'O SED foi projetado com um motor de protocolos abertos. Além de plugins nativos, o SED transmite telemetria e alertas padronizados via <strong>SAPIENT (NATO STANREC 4869)</strong>, <strong>ONVIF (Profile S & M)</strong>, <strong>SIA DC-09</strong>, <strong>REST API / Webhooks</strong>, <strong>MQTT</strong> e <strong>SIP</strong>.',
    consultBtn: 'Consultar arquitetura de sistemas com engenheiros →',
    visitPrefix: 'Visitar',
    customTitle: 'Sistemas Personalizados e de Terceiros',
    customBadge: 'Adaptadores Universais',
    customDesc: 'Conecte qualquer central de comando proprietária, sistema C2 de defesa ou software de segurança com adaptadores padrão.',
    customBtn: 'Solicitar especificações de protocolos →',
    vmsAxis: 'Executa diretamente em câmeras ARTPEC-8/9 ou integra-se ao Axis Camera Station com PTZ automático.',
    vmsMilestone: 'Integração completa de alarmes no Smart Client com classificação acústica ao vivo e mapas.',
    vmsGenetec: 'Injeção direta de eventos para comutação de câmeras, bloqueio de acessos e rotinas de operadores.',
    vmsHanwha: 'Transmissão de metadados HTTP em tempo real com marcadores WAVE e alertas visuais.',
    vmsBosch: 'Integração fluida com o motor de alarmes BVMS e sequenciamento de câmeras via ONVIF/REST.',
    vmsAvigilon: 'Metadados ONVIF Profile S/M com mapeamento Focus of Attention (FoA).',
    vmsHikCentral: 'Vinculação direta de alertas e eventos na central de monitoramento HikCentral e e-maps.',
    vmsGeutebrueck: 'Integração profunda com a plataforma alemã de alta segurança Geutebrück G-Core & G-Sim.',
    vmsAteas: 'Telemetria acústica em tempo real e mapeamento de câmeras no pacote de software ATEAS Security VMS.',
    vmsIvisec: 'Solução integrada com o software de gestão de segurança e CCTV iVISEC desenvolvido pela ZLD.',
    vmsLatis: 'Integração perfeita com plataformas de segurança e controle de acesso LATIS da FIDES.',
    vmsDroneGuard: 'Integração de assinaturas acústicas de drones nos sistemas de defesa C-UAV DroneGuard.',
    vmsKiss: 'Notificação em massa automatizada, chamadas de voz e acionamento por SMS para equipes de resposta.',
    vmsSphereLink: 'Software de IA acústica SED incorporado nativamente em controladores de iluminação pública Tondo SphereLink.'
  },
  ar: {
    videoEyebrow: 'العرض المرئي الرسمي',
    videoTitle: 'شاهد كاشف الأصوات في العمل الفعلي',
    videoDesc: 'شاهد الفيديو الترويجي الرسمي والعروض التقنية التي تستعرض كشف الذكاء الاصطناعي الصوتي في الوقت الفعلي، والتوجيه التلقائي لكاميرات PTZ في أقل من ثانية، والاندماج الكامل في مراكز القيادة الأمنية.',
    subSecondTitle: 'تفعيل في أقل من 0.4 ثانية',
    subSecondDesc: 'تصنيف الأحداث بواسطة الشبكات العصبية وإرسال الإشعارات الطارئة في أقل من 0.4 ثانية.',
    slewTitle: 'توجيه تلقائي لكاميرات PTZ',
    slewDesc: 'توجيه كاميرات PTZ تلقائياً نحو إحداثيات مصدر إطلاق النار أو الصراخ بدقة عالية.',
    gdprTitle: 'الخصوصية بالتصميم (GDPR)',
    gdprDesc: 'معالجة الذكاء الاصطناعي على المعالج الطرفي دون حفظ المحادثات أو التسجيلات الصوتية.',
    demoHeading: 'العروض التقنية والتكامل الميداني',
    v1Title: 'تحديد موقع الأحداث الصوتية والاستجابة السريعة',
    v1Desc: 'تثليث صوتي فائق الدقة وتوجيه تلقائي لكاميرات PTZ أثناء الحوادث الطارئة.',
    v2Title: 'تصميم التغطية وتحسين دقة الكشف',
    v2Desc: 'نمذجة صوتية للموقع وإرشادات توزيع أجهزة الاستشعار في البيئات المعقدة.',
    v3Title: 'JALUD و KISS: الاستجابة الفورية للأزمات',
    v3Desc: 'تكامل مباشر مع أنظمة الإنذار الجماعي والمكالمات الطارئة الآلية ورسائل SMS.',
    vmsEyebrow: 'مصفوفة التكامل المؤسسي والبروتوكولات المفتوحة',
    vmsTitle: 'أنظمة إدارة الفيديو (VMS) والمنصات المدعومة',
    vmsDesc: 'يتكامل كاشف الأصوات SED بسلاسة مع أنظمة VMS العالمية ومنصات PSIM وشبكات الإنذار الجماعي للطوارئ.',
    ecosystemTitle: 'المنظومة المدعومة لأنظمة VMS و PSIM والإنذار الجماعي',
    protoTitle: 'بنية مفتوحة: تكامل سلس مع أي منصة أمنية',
    protoDesc: 'صُمم نظام SED بمحرك بروتوكولات مفتوح. بالإضافة إلى الإضافات البرمجية، يبث SED بيانات قياس موحدة عبر <strong>SAPIENT (NATO STANREC 4869)</strong> و <strong>ONVIF (Profile S & M)</strong> و <strong>SIA DC-09</strong> و <strong>REST API</strong> و <strong>MQTT</strong> و <strong>SIP</strong>.',
    consultBtn: 'استشر مهندسينا حول بنية النظام ←',
    visitPrefix: 'زيارة',
    customTitle: 'الأنظمة المخصصة والأنظمة الخارجية',
    customBadge: 'محولات عالمية',
    customDesc: 'اربط أي غرفة تحكم خاصة أو نظام قيادة دفاعي أو برنامج أمني باستخدام محولات البروتوكول القياسية.',
    customBtn: 'طلب مواصفات البروتوكولات ←',
    vmsAxis: 'يعمل مباشرة على كاميرات ARTPEC-8/9 أو يتكامل مع Axis Camera Station مع توجيه PTZ التلقائي.',
    vmsMilestone: 'تكامل كامل للإنذارات في Smart Client مع التصنيف الصوتي الحي والخرائط التفاعلية.',
    vmsGenetec: 'إرسال مباشر للأحداث للتبديل التلقائي للكاميرات وقفل الأبواب وإجراءات المشغلين.',
    vmsHanwha: 'نقل بيانات الأحداث في الوقت الفعلي عبر HTTP مع علامات مرجعية وتنبيهات WAVE.',
    vmsBosch: 'تكامل سلس مع محرك إنذار BVMS وتسلسل تشغيل الكاميرات عبر ONVIF/REST.',
    vmsAvigilon: 'تكامل بيانات ONVIF Profile S/M مع ميزة Focus of Attention (FoA).',
    vmsHikCentral: 'ربط مباشر للإنذارات في غرفة التحكم المركزية HikCentral والخرائط الإلكترونية.',
    vmsGeutebrueck: 'تكامل عميق مع منصة إدارة الفيديو الألمانية عالية الأمان Geutebrück G-Core & G-Sim.',
    vmsAteas: 'نقل القياس الصوتي في الوقت الفعلي وتوجيه الكاميرات في حزمة ATEAS Security VMS.',
    vmsIvisec: 'حل متكامل وجاهز مع برنامج إدارة الأمن والمراقبة iVISEC من شركة ZLD.',
    vmsLatis: 'تكامل سلس مع منصات الأمان والتحكم في الدخول LATIS من شركة FIDES.',
    vmsDroneGuard: 'دمج البصمة الصوتية للطائرات المسيرة في أنظمة الدفاع الجوي متعددة الحساسات DroneGuard.',
    vmsKiss: 'إنذار جماعي آلي فوري ومكالمات صوتية ورسائل SMS لفرق الطوارئ أثناء الحوادث الحرجة.',
    vmsSphereLink: 'برنامج الذكاء الاصطناعي الصوتي SED مدمج أصلياً في وحدات إنارة الشوارع الذكية Tondo SphereLink.'
  }
};

export function renderSolutionsPage(onNavigate) {
  const container = document.createElement('div');
  container.className = 'sed-page';

  const lang = getLocale() || 'en';
  const s = solutionsTranslations[lang] || solutionsTranslations.en;
  const isCs = lang === 'cs';

  container.innerHTML = `
    <!-- Header -->
    <section class="sed-section" style="background: linear-gradient(165deg, var(--blue-light) 0%, #ffffff 70%); padding: 4.5rem 0 3rem;">
      <div class="sed-container">
        <div class="sed-section-header" style="max-width: 840px;">
          <div class="sed-eyebrow">${t('solutions.eyebrow')}</div>
          <h1 class="sed-page-hero-title">${t('solutions.title')}</h1>
          <p class="sed-section-desc">
            ${t('solutions.subtitle')}
          </p>
        </div>
      </div>
    </section>

    <!-- Architecture Visual Diagram & Interactive Clickable Cards -->
    <section class="sed-section" style="background-color: #ffffff; padding-top: 1rem; padding-bottom: 2rem;">
      <div class="sed-container">
        <div id="solutions-arch-diagram-slot"></div>
      </div>
    </section>

    <!-- Official SED Solution Promo & Technical Demonstrations Video Section -->
    <section class="sed-section" style="background-color: #ffffff; padding: 2rem 0 4rem; border-top: 1px solid var(--slate-line);">
      <div class="sed-container">
        <div class="sed-section-header" style="max-width: 860px;">
          <div class="sed-eyebrow">${s.videoEyebrow}</div>
          <h2 class="sed-section-title">${s.videoTitle}</h2>
          <p class="sed-section-desc">${s.videoDesc}</p>
        </div>

        <!-- Featured Large Promo Video -->
        <div class="sed-card" style="max-width: 980px; margin: 0 auto 3rem; padding: 1.5rem; border-radius: 20px; box-shadow: 0 16px 40px rgba(10, 25, 47, 0.08); border: 1.5px solid var(--slate-line); overflow: hidden; background: #ffffff;">
          <div id="solutions-promo-video-slot"></div>
          
          <div class="sed-grid-3" style="margin-top: 1.75rem; padding: 0.5rem 0.5rem 0.25rem;">
            <div style="display: flex; align-items: flex-start; gap: 12px;">
              <div style="width: 42px; height: 42px; border-radius: 10px; background: rgba(53, 168, 223, 0.12); display: flex; align-items: center; justify-content: center; font-size: 1.25rem; flex-shrink: 0;">⚡</div>
              <div>
                <div style="font-weight: 800; font-size: 0.95rem; color: var(--navy-dark); margin-bottom: 3px;">${s.subSecondTitle}</div>
                <div style="font-size: 0.84rem; color: var(--text-muted); line-height: 1.5;">${s.subSecondDesc}</div>
              </div>
            </div>

            <div style="display: flex; align-items: flex-start; gap: 12px;">
              <div style="width: 42px; height: 42px; border-radius: 10px; background: rgba(53, 168, 223, 0.12); display: flex; align-items: center; justify-content: center; font-size: 1.25rem; flex-shrink: 0;">🎯</div>
              <div>
                <div style="font-weight: 800; font-size: 0.95rem; color: var(--navy-dark); margin-bottom: 3px;">${s.slewTitle}</div>
                <div style="font-size: 0.84rem; color: var(--text-muted); line-height: 1.5;">${s.slewDesc}</div>
              </div>
            </div>

            <div style="display: flex; align-items: flex-start; gap: 12px;">
              <div style="width: 42px; height: 42px; border-radius: 10px; background: rgba(53, 168, 223, 0.12); display: flex; align-items: center; justify-content: center; font-size: 1.25rem; flex-shrink: 0;">🛡️</div>
              <div>
                <div style="font-weight: 800; font-size: 0.95rem; color: var(--navy-dark); margin-bottom: 3px;">${s.gdprTitle}</div>
                <div style="font-size: 0.84rem; color: var(--text-muted); line-height: 1.5;">${s.gdprDesc}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 3 Technical Demonstrations Video Grid -->
        <h3 class="sed-sub-title" style="text-align: center; margin-bottom: 2rem;">
          ${s.demoHeading}
        </h3>
        
        <div class="sed-grid-3">
          
          <!-- Video 1: Sound Event Localization -->
          <div class="sed-card" style="padding: 1.25rem; border-radius: 14px; background: #ffffff; border: 1px solid var(--slate-line); box-shadow: var(--shadow-sm);">
            <div id="solutions-demo-v1-slot" style="margin-bottom: 1rem;"></div>
            <h4 style="font-family: var(--font-heading); font-size: 1.05rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.4rem; line-height: 1.3;">
              ${s.v1Title}
            </h4>
            <p style="font-size: 0.88rem; color: var(--text-muted); line-height: 1.5; margin: 0;">
              ${s.v1Desc}
            </p>
          </div>

          <!-- Video 2: Coverage Design -->
          <div class="sed-card" style="padding: 1.25rem; border-radius: 14px; background: #ffffff; border: 1px solid var(--slate-line); box-shadow: var(--shadow-sm);">
            <div id="solutions-demo-v2-slot" style="margin-bottom: 1rem;"></div>
            <h4 style="font-family: var(--font-heading); font-size: 1.05rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.4rem; line-height: 1.3;">
              ${s.v2Title}
            </h4>
            <p style="font-size: 0.88rem; color: var(--text-muted); line-height: 1.5; margin: 0;">
              ${s.v2Desc}
            </p>
          </div>

          <!-- Video 3: JALUD & KISS -->
          <div class="sed-card" style="padding: 1.25rem; border-radius: 14px; background: #ffffff; border: 1px solid var(--slate-line); box-shadow: var(--shadow-sm);">
            <div id="solutions-demo-v3-slot" style="margin-bottom: 1rem;"></div>
            <h4 style="font-family: var(--font-heading); font-size: 1.05rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.4rem; line-height: 1.3;">
              ${s.v3Title}
            </h4>
            <p style="font-size: 0.88rem; color: var(--text-muted); line-height: 1.5; margin: 0;">
              ${s.v3Desc}
            </p>
          </div>

        </div>
      </div>
    </section>

    <!-- Comprehensive VMS, Command Center & Ecosystem Integrations Grid -->
    <section class="sed-section" style="background-color: var(--bg-alt); padding: 4.5rem 0;">
      <div class="sed-container">
        
        <div class="sed-section-header" style="max-width: 880px;">
          <div class="sed-eyebrow">${s.vmsEyebrow}</div>
          <h2 class="sed-section-title">${s.vmsTitle}</h2>
          <p class="sed-section-desc">
            ${s.vmsDesc}
          </p>
        </div>

        <!-- Hero Protocol Matrix Banner -->
        <div class="sed-proto-banner">
          <div class="sed-detail-hero-grid" style="gap: 2rem;">
            <div>
              <div style="display: inline-block; background: rgba(56, 189, 248, 0.2); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.4); font-size: 0.75rem; font-weight: 800; padding: 0.25rem 0.75rem; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 0.75rem;">
                UNIVERSAL PROTOCOL COMPATIBILITY
              </div>
              <h3 class="sed-sub-title" style="color: #ffffff;">
                ${s.protoTitle}
              </h3>
              <p style="font-size: 0.95rem; color: #cbd5e1; line-height: 1.6; margin-bottom: 0;">
                ${s.protoDesc}
              </p>
            </div>
            <div class="sed-proto-badges">
              <div style="background: rgba(255, 255, 255, 0.07); border: 1px solid rgba(255, 255, 255, 0.12); border-radius: 10px; padding: 0.85rem 1rem;">
                <div style="font-size: 0.88rem; font-weight: 800; color: #38bdf8; margin-bottom: 2px;">🎖️ SAPIENT (NATO)</div>
                <div style="font-size: 0.75rem; color: #94a3b8;">STANREC 4869 & BSI Flex 335</div>
              </div>
              <div style="background: rgba(255, 255, 255, 0.07); border: 1px solid rgba(255, 255, 255, 0.12); border-radius: 10px; padding: 0.85rem 1rem;">
                <div style="font-size: 0.88rem; font-weight: 800; color: #38bdf8; margin-bottom: 2px;">📹 ONVIF Profile S & M</div>
                <div style="font-size: 0.75rem; color: #94a3b8;">Global standard video & audio events</div>
              </div>
              <div style="background: rgba(255, 255, 255, 0.07); border: 1px solid rgba(255, 255, 255, 0.12); border-radius: 10px; padding: 0.85rem 1rem;">
                <div style="font-size: 0.88rem; font-weight: 800; color: #38bdf8; margin-bottom: 2px;">🛡️ SIA DC-09 / IP</div>
                <div style="font-size: 0.75rem; color: #94a3b8;">ARC & alarm center standard</div>
              </div>
              <div style="background: rgba(255, 255, 255, 0.07); border: 1px solid rgba(255, 255, 255, 0.12); border-radius: 10px; padding: 0.85rem 1rem;">
                <div style="font-size: 0.88rem; font-weight: 800; color: #38bdf8; margin-bottom: 2px;">🌐 REST API / JSON</div>
                <div style="font-size: 0.75rem; color: #94a3b8;">Webhooks & custom backends</div>
              </div>
              <div style="background: rgba(255, 255, 255, 0.07); border: 1px solid rgba(255, 255, 255, 0.12); border-radius: 10px; padding: 0.85rem 1rem;">
                <div style="font-size: 0.88rem; font-weight: 800; color: #38bdf8; margin-bottom: 2px;">📡 MQTT & SIP</div>
                <div style="font-size: 0.75rem; color: #94a3b8;">IoT telemetry & PA mass paging</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Integrated Systems Matrix -->
        <h3 class="sed-sub-title" style="margin-bottom: 1.5rem;">
          ${s.ecosystemTitle}
        </h3>

        <div class="sed-grid-3">
          
          <!-- Axis -->
          <div class="sed-card" style="padding: 1.5rem; display: flex; flex-direction: column; justify-content: space-between; border-top: 3px solid var(--blue-primary);">
            <div>
              <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem;">
                <div style="font-size: 1.15rem; font-weight: 800; color: var(--navy-dark);">Axis Communications</div>
                <span style="font-size: 0.72rem; font-weight: 800; color: #0284c7; background: #e0f2fe; padding: 2px 8px; border-radius: 12px;">VMS / Camera</span>
              </div>
              <div style="font-size: 0.82rem; color: var(--blue-primary); font-weight: 700; margin-bottom: 0.5rem;">ACAP v4 Native Integration & ACS</div>
              <p style="font-size: 0.85rem; color: var(--text-dark); line-height: 1.5; margin-bottom: 0.75rem;">
                ${s.vmsAxis}
              </p>
            </div>
          </div>

          <!-- Milestone -->
          <div class="sed-card" style="padding: 1.5rem; display: flex; flex-direction: column; justify-content: space-between; border-top: 3px solid var(--blue-primary);">
            <div>
              <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem;">
                <div style="font-size: 1.15rem; font-weight: 800; color: var(--navy-dark);">Milestone Systems</div>
                <span style="font-size: 0.72rem; font-weight: 800; color: #0284c7; background: #e0f2fe; padding: 2px 8px; border-radius: 12px;">Global VMS</span>
              </div>
              <div style="font-size: 0.82rem; color: var(--blue-primary); font-weight: 700; margin-bottom: 0.5rem;">XProtect Certified Plugin & MIP SDK</div>
              <p style="font-size: 0.85rem; color: var(--text-dark); line-height: 1.5; margin-bottom: 0.75rem;">
                ${s.vmsMilestone}
              </p>
            </div>
          </div>

          <!-- Genetec -->
          <div class="sed-card" style="padding: 1.5rem; display: flex; flex-direction: column; justify-content: space-between; border-top: 3px solid var(--blue-primary);">
            <div>
              <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem;">
                <div style="font-size: 1.15rem; font-weight: 800; color: var(--navy-dark);">Genetec</div>
                <span style="font-size: 0.72rem; font-weight: 800; color: #0284c7; background: #e0f2fe; padding: 2px 8px; border-radius: 12px;">Global VMS</span>
              </div>
              <div style="font-size: 0.82rem; color: var(--blue-primary); font-weight: 700; margin-bottom: 0.5rem;">Security Center External Event SDK</div>
              <p style="font-size: 0.85rem; color: var(--text-dark); line-height: 1.5; margin-bottom: 0.75rem;">
                ${s.vmsGenetec}
              </p>
            </div>
          </div>

          <!-- Hanwha Vision -->
          <div class="sed-card" style="padding: 1.5rem; display: flex; flex-direction: column; justify-content: space-between; border-top: 3px solid var(--blue-primary);">
            <div>
              <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem;">
                <div style="font-size: 1.15rem; font-weight: 800; color: var(--navy-dark);">Hanwha Vision</div>
                <span style="font-size: 0.72rem; font-weight: 800; color: #0284c7; background: #e0f2fe; padding: 2px 8px; border-radius: 12px;">Global VMS</span>
              </div>
              <div style="font-size: 0.82rem; color: var(--blue-primary); font-weight: 700; margin-bottom: 0.5rem;">Wisenet WAVE VMS Metadata Push</div>
              <p style="font-size: 0.85rem; color: var(--text-dark); line-height: 1.5; margin-bottom: 0.75rem;">
                ${s.vmsHanwha}
              </p>
            </div>
          </div>

          <!-- Bosch -->
          <div class="sed-card" style="padding: 1.5rem; display: flex; flex-direction: column; justify-content: space-between; border-top: 3px solid var(--blue-primary);">
            <div>
              <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem;">
                <div style="font-size: 1.15rem; font-weight: 800; color: var(--navy-dark);">Bosch Security Systems</div>
                <span style="font-size: 0.72rem; font-weight: 800; color: #0284c7; background: #e0f2fe; padding: 2px 8px; border-radius: 12px;">Enterprise VMS</span>
              </div>
              <div style="font-size: 0.82rem; color: var(--blue-primary); font-weight: 700; margin-bottom: 0.5rem;">Bosch Video Management System (BVMS)</div>
              <p style="font-size: 0.85rem; color: var(--text-dark); line-height: 1.5; margin-bottom: 0.75rem;">
                ${s.vmsBosch}
              </p>
            </div>
          </div>

          <!-- Avigilon -->
          <div class="sed-card" style="padding: 1.5rem; display: flex; flex-direction: column; justify-content: space-between; border-top: 3px solid var(--blue-primary);">
            <div>
              <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem;">
                <div style="font-size: 1.15rem; font-weight: 800; color: var(--navy-dark);">Avigilon (Motorola Solutions)</div>
                <span style="font-size: 0.72rem; font-weight: 800; color: #0284c7; background: #e0f2fe; padding: 2px 8px; border-radius: 12px;">Enterprise VMS</span>
              </div>
              <div style="font-size: 0.82rem; color: var(--blue-primary); font-weight: 700; margin-bottom: 0.5rem;">Avigilon Unity Video & Control Center (ACC)</div>
              <p style="font-size: 0.85rem; color: var(--text-dark); line-height: 1.5; margin-bottom: 0.75rem;">
                ${s.vmsAvigilon}
              </p>
            </div>
          </div>

          <!-- HikCentral -->
          <div class="sed-card" style="padding: 1.5rem; display: flex; flex-direction: column; justify-content: space-between; border-top: 3px solid var(--blue-primary);">
            <div>
              <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem;">
                <div style="font-size: 1.15rem; font-weight: 800; color: var(--navy-dark);">Hikvision HikCentral</div>
                <span style="font-size: 0.72rem; font-weight: 800; color: #0284c7; background: #e0f2fe; padding: 2px 8px; border-radius: 12px;">Enterprise VMS</span>
              </div>
              <div style="font-size: 0.82rem; color: var(--blue-primary); font-weight: 700; margin-bottom: 0.5rem;">HikCentral Professional Event Integration</div>
              <p style="font-size: 0.85rem; color: var(--text-dark); line-height: 1.5; margin-bottom: 0.75rem;">
                ${s.vmsHikCentral}
              </p>
            </div>
          </div>

          <!-- Geutebrück -->
          <div class="sed-card" style="padding: 1.5rem; display: flex; flex-direction: column; justify-content: space-between; border-top: 3px solid var(--blue-primary);">
            <div>
              <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem;">
                <div style="font-size: 1.15rem; font-weight: 800; color: var(--navy-dark);">Geutebrück</div>
                <span style="font-size: 0.72rem; font-weight: 800; color: #0284c7; background: #e0f2fe; padding: 2px 8px; border-radius: 12px;">Enterprise VMS</span>
              </div>
              <div style="font-size: 0.82rem; color: var(--blue-primary); font-weight: 700; margin-bottom: 0.5rem;">G-Core & G-Sim Video Security</div>
              <p style="font-size: 0.85rem; color: var(--text-dark); line-height: 1.5; margin-bottom: 0.75rem;">
                ${s.vmsGeutebrueck}
              </p>
            </div>
            <a href="https://www.geutebrueck.com/" target="_blank" rel="noopener noreferrer" style="font-size: 0.8rem; font-weight: 700; color: var(--blue-primary); text-decoration: none; display: inline-flex; align-items: center; gap: 4px;">
              ${s.visitPrefix} Geutebrück →
            </a>
          </div>

          <!-- Ateas -->
          <div class="sed-card" style="padding: 1.5rem; display: flex; flex-direction: column; justify-content: space-between; border-top: 3px solid var(--blue-primary);">
            <div>
              <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem;">
                <div style="font-size: 1.15rem; font-weight: 800; color: var(--navy-dark);">ATEAS</div>
                <span style="font-size: 0.72rem; font-weight: 800; color: #0284c7; background: #e0f2fe; padding: 2px 8px; border-radius: 12px;">Enterprise VMS</span>
              </div>
              <div style="font-size: 0.82rem; color: var(--blue-primary); font-weight: 700; margin-bottom: 0.5rem;">ATEAS Security VMS Integration</div>
              <p style="font-size: 0.85rem; color: var(--text-dark); line-height: 1.5; margin-bottom: 0.75rem;">
                ${s.vmsAteas}
              </p>
            </div>
            <a href="https://www.ateas.net/" target="_blank" rel="noopener noreferrer" style="font-size: 0.8rem; font-weight: 700; color: var(--blue-primary); text-decoration: none; display: inline-flex; align-items: center; gap: 4px;">
              ${s.visitPrefix} ATEAS →
            </a>
          </div>

          <!-- iVISEC (ZLD) -->
          <div class="sed-card" style="padding: 1.5rem; display: flex; flex-direction: column; justify-content: space-between; border-top: 3px solid var(--blue-primary);">
            <div>
              <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem;">
                <div style="font-size: 1.15rem; font-weight: 800; color: var(--navy-dark);">iVISEC (ZLD)</div>
                <span style="font-size: 0.72rem; font-weight: 800; color: #0284c7; background: #e0f2fe; padding: 2px 8px; border-radius: 12px;">PSIM / VMS</span>
              </div>
              <div style="font-size: 0.82rem; color: var(--blue-primary); font-weight: 700; margin-bottom: 0.5rem;">Enterprise Video & Security Management</div>
              <p style="font-size: 0.85rem; color: var(--text-dark); line-height: 1.5; margin-bottom: 0.75rem;">
                ${s.vmsIvisec}
              </p>
            </div>
            <a href="https://www.zld.cz/" target="_blank" rel="noopener noreferrer" style="font-size: 0.8rem; font-weight: 700; color: var(--blue-primary); text-decoration: none; display: inline-flex; align-items: center; gap: 4px;">
              ${s.visitPrefix} ZLD iVISEC →
            </a>
          </div>

          <!-- LATIS (FIDES) -->
          <div class="sed-card" style="padding: 1.5rem; display: flex; flex-direction: column; justify-content: space-between; border-top: 3px solid var(--blue-primary);">
            <div>
              <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem;">
                <div style="font-size: 1.15rem; font-weight: 800; color: var(--navy-dark);">LATIS (FIDES)</div>
                <span style="font-size: 0.72rem; font-weight: 800; color: #0284c7; background: #e0f2fe; padding: 2px 8px; border-radius: 12px;">Integrated PSIM</span>
              </div>
              <div style="font-size: 0.82rem; color: var(--blue-primary); font-weight: 700; margin-bottom: 0.5rem;">Comprehensive Security & Access Management</div>
              <p style="font-size: 0.85rem; color: var(--text-dark); line-height: 1.5; margin-bottom: 0.75rem;">
                ${s.vmsLatis}
              </p>
            </div>
            <a href="https://www.fides.cz/en/latis" target="_blank" rel="noopener noreferrer" style="font-size: 0.8rem; font-weight: 700; color: var(--blue-primary); text-decoration: none; display: inline-flex; align-items: center; gap: 4px;">
              ${s.visitPrefix} FIDES LATIS →
            </a>
          </div>

          <!-- DroneGuard -->
          <div class="sed-card" style="padding: 1.5rem; display: flex; flex-direction: column; justify-content: space-between; border-top: 3px solid var(--navy-dark);">
            <div>
              <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem;">
                <div style="font-size: 1.15rem; font-weight: 800; color: var(--navy-dark);">DroneGuard</div>
                <span style="font-size: 0.72rem; font-weight: 800; color: #1e293b; background: #e2e8f0; padding: 2px 8px; border-radius: 12px;">C-UAV Airspace</span>
              </div>
              <div style="font-size: 0.82rem; color: var(--navy-dark); font-weight: 700; margin-bottom: 0.5rem;">Perimeter Airspace & Drone Countermeasures</div>
              <p style="font-size: 0.85rem; color: var(--text-dark); line-height: 1.5; margin-bottom: 0.75rem;">
                ${s.vmsDroneGuard}
              </p>
            </div>
            <a href="https://droneguard.com/" target="_blank" rel="noopener noreferrer" style="font-size: 0.8rem; font-weight: 700; color: var(--blue-primary); text-decoration: none; display: inline-flex; align-items: center; gap: 4px;">
              ${s.visitPrefix} DroneGuard →
            </a>
          </div>

          <!-- KISS (Svolávací systém) -->
          <div class="sed-card" style="padding: 1.5rem; display: flex; flex-direction: column; justify-content: space-between; border-top: 3px solid #e6273e;">
            <div>
              <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem;">
                <div style="font-size: 1.15rem; font-weight: 800; color: var(--navy-dark);">KISS (Svolávač.cz)</div>
                <span style="font-size: 0.72rem; font-weight: 800; color: #e6273e; background: #fee2e2; padding: 2px 8px; border-radius: 12px;">Mass Notification</span>
              </div>
              <div style="font-size: 0.82rem; color: #e6273e; font-weight: 700; margin-bottom: 0.5rem;">Automated Emergency Calling & SMS Dispatch</div>
              <p style="font-size: 0.85rem; color: var(--text-dark); line-height: 1.5; margin-bottom: 0.75rem;">
                ${s.vmsKiss}
              </p>
            </div>
            <a href="https://www.svolavac.cz/" target="_blank" rel="noopener noreferrer" style="font-size: 0.8rem; font-weight: 700; color: #e6273e; text-decoration: none; display: inline-flex; align-items: center; gap: 4px;">
              ${s.visitPrefix} Svolávač.cz (KISS) →
            </a>
          </div>

          <!-- Tondo SphereLink -->
          <div class="sed-card" style="padding: 1.5rem; display: flex; flex-direction: column; justify-content: space-between; border-top: 3px solid #8b5cf6;">
            <div>
              <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem;">
                <div style="font-size: 1.15rem; font-weight: 800; color: var(--navy-dark);">Tondo SphereLink</div>
                <span style="font-size: 0.72rem; font-weight: 800; color: #7c3aed; background: #ede9fe; padding: 2px 8px; border-radius: 12px;">Smart City IoT</span>
              </div>
              <div style="font-size: 0.82rem; color: #7c3aed; font-weight: 700; margin-bottom: 0.5rem;">Smart Lighting & Municipal IoT Controllers</div>
              <p style="font-size: 0.85rem; color: var(--text-dark); line-height: 1.5; margin-bottom: 0.75rem;">
                ${s.vmsSphereLink}
              </p>
            </div>
            <a href="https://spherelink.ai/" target="_blank" rel="noopener noreferrer" style="font-size: 0.8rem; font-weight: 700; color: #7c3aed; text-decoration: none; display: inline-flex; align-items: center; gap: 4px;">
              ${s.visitPrefix} SphereLink.ai →
            </a>
          </div>

          <!-- Custom & Universal Integration Card -->
          <div class="sed-card" style="padding: 1.5rem; display: flex; flex-direction: column; justify-content: space-between; border-top: 3px solid #10b981; background: #f0fdf4;">
            <div>
              <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem;">
                <div style="font-size: 1.15rem; font-weight: 800; color: #166534;">${s.customTitle}</div>
                <span style="font-size: 0.72rem; font-weight: 800; color: #166534; background: #dcfce7; padding: 2px 8px; border-radius: 12px;">${s.customBadge}</span>
              </div>
              <div style="font-size: 0.82rem; color: #15803d; font-weight: 700; margin-bottom: 0.5rem;">ONVIF Profile S/M, SAPIENT (NATO), SIA DC-09 & REST</div>
              <p style="font-size: 0.85rem; color: #14532d; line-height: 1.5; margin-bottom: 0.75rem;">
                ${s.customDesc}
              </p>
            </div>
            <a href="#contact" class="sed-btn sed-btn-primary" style="text-align: center; font-size: 0.8rem; padding: 0.5rem 1rem;" data-route="contact">
              ${s.customBtn}
            </a>
          </div>

        </div>

        <div style="text-align: center; margin-top: 3.5rem;">
          <a href="#contact" class="sed-btn sed-btn-hero sed-btn-primary" data-route="contact">${s.consultBtn}</a>
        </div>
      </div>
    </section>
  `;

  // Attach architecture graph
  const archSlot = container.querySelector('#solutions-arch-diagram-slot');
  if (archSlot) {
    const archGraph = renderArchitectureGraph(onNavigate);
    if (archGraph && archGraph instanceof Node) {
      archSlot.appendChild(archGraph);
    }
  }

  // Mount Lazy & Privacy-Compliant YouTube Embeds
  const promoSlot = container.querySelector('#solutions-promo-video-slot');
  if (promoSlot) {
    promoSlot.appendChild(createYouTubeEmbed({
      videoId: 'GTbZe57l75Q',
      title: s.videoTitle,
      borderRadius: '14px'
    }));
  }

  const v1Slot = container.querySelector('#solutions-demo-v1-slot');
  if (v1Slot) {
    v1Slot.appendChild(createYouTubeEmbed({
      videoId: 'hL5uo1kZkU4',
      title: s.v1Title,
      borderRadius: '8px'
    }));
  }

  const v2Slot = container.querySelector('#solutions-demo-v2-slot');
  if (v2Slot) {
    v2Slot.appendChild(createYouTubeEmbed({
      videoId: 'quk8DXZKkeE',
      title: s.v2Title,
      borderRadius: '8px'
    }));
  }

  const v3Slot = container.querySelector('#solutions-demo-v3-slot');
  if (v3Slot) {
    const v3Id = isCs ? 'ZVXmUpzUh1U' : '6Wb7wwi00Z8';
    v3Slot.appendChild(createYouTubeEmbed({
      videoId: v3Id,
      title: s.v3Title,
      borderRadius: '8px'
    }));
  }

  container.querySelectorAll('[data-route]').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      if (onNavigate) onNavigate(link.getAttribute('data-route'));
    });
  });

  return container;
}
